import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import Swal from 'sweetalert2';
import BOSS_mainlogo from '../../../../assets/icons/BOSS_mainlogo.svg';
import './PhoneNumberVerification.scss';
import PostRequest from '../../../../service/PostRequest';
import GetRequest from '../../../../service/GetRequest';

const PhoneNumberVerification = () => {
  const location = useLocation();
  const toastBottomCenter = useRef(null);

  const phoneNumber = location.state?.phoneNumber;
  const navigate = useNavigate();
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const inputRefs = useRef([]);
  const [showOTPError, setShowOTPError] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [remainingTime, setRemainingTime] = useState(15 * 60); 

  useEffect(() => {
    const countdown = setInterval(() => {
      setRemainingTime(prevTime => prevTime - 1);
    }, 1000);

    return () => clearInterval(countdown);
  }, []);

  useEffect(() => {
    if (remainingTime === 0) {
      setShowOTPError(true);
    }
  }, [remainingTime]);

  const handlePaste = (event) => {
    const paste = event.clipboardData.getData('text');
    if (paste.length === otp.length && /^\d+$/.test(paste)) {
      const newOtp = paste.split('');
      setOtp(newOtp);
      newOtp.forEach((digit, index) => {
        inputRefs.current[index].value = digit;
      });
      inputRefs.current[otp.length - 1].focus();
      verifyOtp(newOtp.join(''));
    }
  };

  const handleChange = (element, index) => {
    const value = element.value;
    if (/^[0-9]$/.test(value) || value === "") {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value !== "" && index < 5) {
        inputRefs.current[index + 1].focus();
      }

      if (index === 5 && newOtp.join('').length === 6) {
        verifyOtp(newOtp.join(''));
      }
    }
  };

  const handleKeyDown = (event, index) => {
    if (event.key === "Backspace" && index > 0 && otp[index] === "") {
      inputRefs.current[index - 1].focus();
    }
    setShowOTPError(false);
  };

  const verifyOtp = async (otp) => {
    setIsVerifying(true);
    const data = {
      otp: otp,
      phoneNumber: `+${phoneNumber}`,
    };
    try {
      const response = await PostRequest('auth/verify/account', data, false);
      if (response) {
        setIsVerified(true);
        Swal.fire({
          title: "OTP verified!",
          text: "Continue to Login",
          icon: "success"
        }).then(() => {
          navigate("/login");
        });
      } else {
        setShowOTPError(false);
        showMessage("OTP Verification failed", toastBottomCenter, 'info');
      }
    } catch (error) {
      console.error('Error in postData:', error);
      showMessage(error.message, toastBottomCenter, 'info');
    }

    setIsVerifying(false);
  };

  const showMessage = (message, ref, severity) => {
    ref.current.show({ severity: severity, summary: message, detail: message, life: 3000 });
  };

  const resendOTP = async () => {
    await GetRequest(`auth/validate/otp/request?emailOrWhatsapp=${phoneNumber}`)
      .then(response => {
        if (response) {
              GetRequest(`auth/resend/otp?phoneNumber=${phoneNumber}`)
                .then(response => {
                  if (response) {
                    Swal.fire({
                      title: "OTP has been sent!",
                      text: "An OTP has been sent to your registered Whatsapp Number -" + phoneNumber,
                      icon: "success"
                    });
                  } else {
                    showMessage("OTP resend failed", toastBottomCenter, 'info');
                    setShowOTPError(false);
                  }
                });
              } else {
                setShowOTPError(false);
                showMessage("Too many OTP requests. Please try again later.", toastBottomCenter, 'info');
        }
      });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  return (
    <div className='VerifyEmailContainer'>
      <Toast ref={toastBottomCenter} position="bottom-center" />
      <div className='Heading'>
        <img src={BOSS_mainlogo} alt="BOSS Logo" />
        <h2>Please Verify your Whatsapp Number</h2>
      </div>
      <div className='WholeForm'>
        {isVerified ? (
          <div className="text-center mt-4">
            <h3>Whatsapp Number verified, redirecting you to Login in 3 seconds</h3>
            <p>If you are not redirected in 3 seconds, please
              <span
                style={{ color: 'blue', fontWeight: 'bold', cursor: 'pointer' }}
                onClick={() => { navigate("/login"); }}
              > click here</span> to login
            </p>
          </div>
        ) : (
          <div className="text-center mt-30">
            <p style={{padding:"0px 80px"}}>Please enter the OTP sent to your Whatsapp Number +{phoneNumber} to verify your account.</p>
            <div className="otp-container" onPaste={handlePaste}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={e => handleChange(e.target, index)}
                  onKeyDown={e => handleKeyDown(e, index)}
                  ref={el => inputRefs.current[index] = el}
                  className="otp-input"
                />
              ))}
            </div>
            <p>Time remaining: {formatTime(remainingTime)}</p>
            <button onClick={() => verifyOtp(otp.join(''))} className="verify-button" disabled={isVerifying || remainingTime === 0}>
              {isVerifying ? 'Please wait...' : 'Verify OTP'}
            </button>
            <p className="mt-3">
              <span
                style={{ color: {remainingTime}, fontWeight: 'bold', cursor: 'pointer' }}
                onClick={resendOTP}
              > Resend OTP</span>
            </p>
            {showOTPError && (
              <div className="error-message">
                OTP Expired or failed
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PhoneNumberVerification;
